import Formatters from '@dashhudson/dashing-ui/utils/formatters';
import dayjs from 'dayjs';
import cloneDeep from 'lodash/cloneDeep';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';
import { floor, map, pick } from 'lodash-es';
import { computed } from 'vue';

import { useDashboards } from '@/app/dashboards/composables/useDashboards';
import { useHorizontalBarReportDetails } from '@/app/dashboards/composables/useHorizontalBarReportDetails';
import { useMetricDetails } from '@/app/dashboards/composables/useMetricDetails';
import { useMetricFormatting } from '@/app/dashboards/composables/useMetricFormattingSetup';
import { useReport } from '@/app/dashboards/composables/useReport';
import { useReportCategory } from '@/app/dashboards/composables/useReportCategory';
import { useReportMetaAggregateBy } from '@/app/dashboards/composables/useReportMetaAggregateBy';
import { useReportType } from '@/app/dashboards/composables/useReportType';
import {
  ALL_DATA_TYPES,
  CHART_DATA_LIMIT,
  DATA_TYPES,
  LISTENING_DATA_TYPES,
  ONLINE_FOLLOWER_HOUR_BUCKET_SIZE,
  sentimentColorMap,
  TAG_BASED_AGGREGATE_TYPES,
  TAG_BASED_DATA_TYPES,
  TOTAL_METRIC_GROUPS,
} from '@/app/dashboards/constants';
import { canAccessCompetitor } from '@/app/dashboards/utils/competitive.utils';
import { formatGoal } from '@/app/dashboards/utils/report.utils';
import { GRAPH_REPORT_TYPES, METRIC_TYPE_REPORT_KEYS } from '@/app/dashboards/utils/reports.enum';
import { useCustomMetrics } from '@/app/settings/composables/customMetrics';
import { sortChartData } from '@/components/foundation/charts/chartjs.utils';
import {
  AGGREGATE_REPORT_TYPES,
  BASE_BREAKDOWN_OPTIONS,
  LISTENING_REPORT_AGGREGATE_BY_TYPES,
} from '@/models/dashboards/aggregate-type.enum';
import {
  BENCHMARK_TYPES,
  INDUSTRY_FOLLOWER_BUCKETS,
} from '@/models/dashboards/benchmark-type.enum';
import {
  ALL_CHANNELS_KEY,
  getChannelConfigProp,
  getChannelText,
} from '@/models/dashboards/channels.enum';
import { GRAPH_SCALES } from '@/models/dashboards/graph-scales.enum';
import { ONLINE_FOLLOWERS_METRIC } from '@/models/dashboards/metrics.constants.js';
import { useAuthStore } from '@/stores/auth';
import { useDashboardReportsStore } from '@/stores/dashboards-reports';
import { DEFAULT_TIME_FORMAT, TIME_FORMATS } from '@/utils/chartjs-loader';
import { formatDatePattern } from '@/utils/formatters';

const hasChannelBucketShape = (root) =>
  Object.values(root).some((bucket) => bucket?.data_type === DATA_TYPES.CHANNEL);

const hasAllChannelsInnerKey = (root) => {
  // Find a bucket that actually has metrics (skip BRAND entries without metrics)
  const firstBucketWithMetrics = Object.values(root).find(
    (b) => b && typeof b === 'object' && b.metrics,
  );
  const firstMetric = Object.values(firstBucketWithMetrics?.metrics ?? {})[0];
  return !!firstMetric && Object.prototype.hasOwnProperty.call(firstMetric, ALL_CHANNELS_KEY);
};

/**
 * Returns array of data_types to include based on aggregation.
 * @param {string|null} aggregateBy - The aggregate_by value from report meta
 * @returns {string[]|null} Array of allowed data types (uppercase), or null if no filtering
 */
function getAllowedDataTypes(aggregateBy) {
  // BRAND aggregation includes competitor data (competitive reports use this)
  // Return null to include everything - brands, competitors, competitor averages
  if (aggregateBy?.toUpperCase() === DATA_TYPES.BRAND) {
    return null;
  }

  // If aggregate_by is set and is a known data type, filter to it
  if (aggregateBy) {
    const allDataTypesUpper = Object.values(ALL_DATA_TYPES).map((dt) => dt.toUpperCase());
    if (allDataTypesUpper.includes(aggregateBy.toUpperCase())) {
      return [aggregateBy.toUpperCase()];
    }
  }

  // No filtering (old flow or unknown aggregation)
  return null;
}

/**
 * Filter predicate for buckets based on allowed data types.
 * @param {object} bucket - The bucket to check
 * @param {string[]|null} allowedDataTypes - Array of allowed data types, or null for no filtering
 * @returns {boolean} Whether the bucket should be included
 */
function shouldIncludeBucket(bucket, allowedDataTypes) {
  // Skip non-object entries or entries without data_type (like top-level 'metrics')
  if (!bucket || typeof bucket !== 'object' || !bucket.data_type) {
    return false;
  }

  // If no filtering required, include all with data_type
  if (!allowedDataTypes) {
    return true;
  }

  // Match data_type to allowed list (case-insensitive)
  return allowedDataTypes.some(
    (allowed) => allowed.toUpperCase() === bucket.data_type.toUpperCase(),
  );
}

/**
 * Returns the appropriate channel key for a bucket with fallback logic.
 * @param {string|null} aggregateBy - The aggregate_by value
 * @param {string|null} bucketKey - The bucket key (used for CHANNEL aggregation)
 * @returns {string} The channel key to use
 */
function getChannelKeyForBucket(aggregateBy, bucketKey) {
  if (aggregateBy === TOTAL_METRIC_GROUPS.CHANNEL || aggregateBy === 'CHANNEL') {
    return bucketKey;
  }
  return ALL_CHANNELS_KEY;
}

/**
 * Extracts time series data from a bucket with fallback logic.
 * @param {object} bucket - The bucket containing metrics
 * @param {string} metric - The metric key to extract
 * @param {string} primaryChannelKey - The primary channel key to try
 * @param {string} fallbackChannelKey - Fallback channel key if primary not found
 * @returns {object|undefined} The time series data, or undefined if not found
 */
function extractTimeSeries(bucket, metric, primaryChannelKey, fallbackChannelKey) {
  return (
    bucket.metrics?.[metric]?.[primaryChannelKey] ??
    bucket.metrics?.[metric]?.[ALL_CHANNELS_KEY] ??
    bucket.metrics?.[metric]?.[fallbackChannelKey] ??
    bucket.metrics?.[metric] // old format fallback (no channel nesting)
  );
}

export function useGraphReportDetails({ report, graphScale } = {}) {
  const authStore = useAuthStore();
  const dashboardReportsStore = useDashboardReportsStore();
  const { canAccessStatsEngineV2 } = useDashboards();

  const {
    aggregateBy,
    availableAssignees,
    secondaryAggregateBy,
    firstChannel,
    firstMetric,
    isCustomMetricReportTile,
    isMultiChannel,
    isMultiMetric,
    metrics,
    reportCategory,
    reportChannels,
    reportMeta,
    showGoalChip,
    reportData,
    formattedReportDataForTableAndGraphReports,
    reportMetricDetails,
    reportType,
    currency,
    displayStrategy,
  } = useReport({
    report,
  });

  const { disableAggregationSort } = useReportMetaAggregateBy({ aggregateBy });

  const { dataType } = useMetricDetails({
    metric: firstMetric,
    channels: reportChannels,
    metricReportTypeKey: METRIC_TYPE_REPORT_KEYS.GRAPH_REPORT,
  });

  const { formatCustomMetricValue, getCustomReportDetailsFromId } = useCustomMetrics();
  const { isCommunityReportCategory, isTSIReportCategory, isListeningReportCategory } =
    useReportCategory({ reportCategory });
  const {
    isCreatorReport,
    isLineChart,
    isStackedBarChart,
    isBarChart,
    isAdsStackedBarChart,
    isDoughnutChart,
    isHorizontalBarChart,
  } = useReportType({
    reportType,
  });

  const isTimeSeriesStackedBarChart = computed(
    () => isStackedBarChart.value && firstMetric.value === ONLINE_FOLLOWERS_METRIC,
  );

  const customMetricDetails = computed(() => getCustomReportDetailsFromId(firstMetric.value));

  const { getFormatConfig, isDurationMetric } = useMetricFormatting({
    dataType,
    isCustomMetricReportTile,
    customMetricDetails,
    currency,
    displayStrategy,
  });
  // TODO [SC:177226] move configs to formatMetricValue so that the composable isn't called in as many places
  function getFormattedValue(value, isHover = false) {
    if (isCustomMetricReportTile.value)
      return formatCustomMetricValue(value, customMetricDetails.value.metricFormat);

    const config = getFormatConfig({
      value,
      disableAbbreviation: true,
      isHover,
      isDoughnutChart: isDoughnutChart.value,
    });
    const formatTypeMap = {
      percent: Formatters.FORMAT_TYPES.PERCENT,
      currency: Formatters.FORMAT_TYPES.CURRENCY,
      number: Formatters.FORMAT_TYPES.NUMBER,
      duration: Formatters.FORMAT_TYPES.DURATION,
      abbr: Formatters.FORMAT_TYPES.ABBREVIATED,
      normal: Formatters.FORMAT_TYPES.NUMBER,
    };
    const formatterType = formatTypeMap[config.format] || Formatters.FORMAT_TYPES.NUMBER;

    return Formatters.formatNumber(value, formatterType, {
      formatOptions: config.formatOptions,
      durationOptions: config.durationOptions,
    });
  }

  const chartFormatConfig = computed(() =>
    getFormatConfig({
      value: undefined,
      disableAbbreviation: true,
      isDoughnutChart: isDoughnutChart.value,
    }),
  );

  const useChannelDataset = computed(
    () =>
      aggregateBy.value === TOTAL_METRIC_GROUPS.CHANNEL ||
      (isLineChart.value &&
        isMultiChannel.value &&
        !isCustomMetricReportTile.value &&
        !isListeningReportCategory.value),
  );

  const useMetricDataset = computed(() => isLineChart.value && isMultiMetric.value);

  const useTags = computed(
    () =>
      aggregateBy.value === TOTAL_METRIC_GROUPS.TAG ||
      aggregateBy.value === TOTAL_METRIC_GROUPS.ACCOUNT_TAG,
  );

  const useAgeGenderDataset = computed(() => aggregateBy.value === TOTAL_METRIC_GROUPS.AGE_GENDER);

  const graphType = computed(() => {
    if (isLineChart.value) return GRAPH_REPORT_TYPES.LINE;
    if (isHorizontalBarChart.value) return GRAPH_REPORT_TYPES.HORIZONTAL_BAR;
    if (isStackedBarChart.value) return GRAPH_REPORT_TYPES.STACKED_BAR;
    if (isBarChart.value) return GRAPH_REPORT_TYPES.BAR;
    if (isDoughnutChart.value) return GRAPH_REPORT_TYPES.DOUGHNUT;
    return {};
  });

  const timeUnit = computed(() => GRAPH_SCALES?.[graphScale?.value]?.chartJSTimeUnit);

  const showCompetitorAverage = computed(() => {
    const data = reportData.value ?? [];
    const competitorCount = Object.values(data).reduce((acc, accountData) => {
      if (accountData?.data_type === DATA_TYPES.COMPETITOR) return acc + 1;
      return acc;
    }, 0);
    return competitorCount > 1;
  });

  const goalLineOptions = computed(() => {
    if (!showGoalChip.value) return {};
    return {
      plugins: {
        markerLine: {
          markers: [
            {
              ...formatGoal(
                reportMeta.value.goals?.overall?.type,
                reportMeta.value.goals?.overall?.value,
              ),
              legend: {
                label: 'Set Goal',
              },
            },
          ],
        },
      },
    };
  });

  const goalValues = computed(() => {
    if (!showGoalChip.value) return [];
    const formattedGoal = formatGoal(
      reportMeta.value.goals?.overall?.type,
      reportMeta.value.goals?.overall?.value,
    );
    return formattedGoal?.value != null ? [formattedGoal.value] : [];
  });

  const competitorTagsById = computed(() => {
    if (!useTags.value) return {};

    return dashboardReportsStore.competitorTags.reduce((acc, brandData) => {
      const brandCompetitorTagsById = (brandData?.tags ?? []).reduce((innerAcc, tagData) => {
        innerAcc[tagData.id] = tagData;
        return innerAcc;
      }, {});
      return { ...acc, ...brandCompetitorTagsById };
    }, {});
  });

  function filterAccountsWithTagDataType(accountData) {
    // TAG aggregation is special: aggregate_by is 'TAG' but data_type is 'ACCOUNT_TAG' or 'TAG'
    if (useTags.value) {
      return TAG_BASED_DATA_TYPES.includes(accountData?.data_type);
    }

    const allowedDataTypes = getAllowedDataTypes(aggregateBy.value);

    // If no filtering required (old flow, BRAND aggregation, or unknown), include everything
    if (!allowedDataTypes) {
      return true;
    }

    return shouldIncludeBucket(accountData, allowedDataTypes);
  }

  function filterAccountsWithoutAccess(accountData) {
    const skipDataTypes = [
      DATA_TYPES.BENCHMARK,
      DATA_TYPES.PUBLISHER_PLATFORM,
      DATA_TYPES.COUNTRY,
      DATA_TYPES.REGION,
      DATA_TYPES.AGE_GENDER,
      DATA_TYPES.TAG,
      DATA_TYPES.ACCOUNT_TAG,
      DATA_TYPES.CONTENT_TAG,
      DATA_TYPES.SENTIMENT,
      DATA_TYPES.MESSAGE_TYPE,
      DATA_TYPES.ASSIGNEE,
      DATA_TYPES.CHANNEL,
      DATA_TYPES.FOLLOWER_STATUS,
      ...Object.values(LISTENING_DATA_TYPES),
    ];

    const uniqueSkipDataTypes = [...new Set(skipDataTypes)];
    if (uniqueSkipDataTypes.includes(accountData?.data_type)) return true;
    if (accountData?.data_type === DATA_TYPES.COMPETITOR) {
      return accountData?.user_has_access && canAccessCompetitor(accountData);
    }
    return accountData?.user_has_access;
  }

  function filterCompetitorAverage(accountData) {
    return accountData?.data_type !== DATA_TYPES.COMPETITOR_AVERAGE || showCompetitorAverage.value;
  }

  function filterOutBrandForCreatorReport(accountData) {
    return accountData?.data_type !== DATA_TYPES.BRAND || !isCreatorReport.value;
  }

  function getXAndYData(rawData, label) {
    const formattedData = isLineChart.value ? rawData : pick(rawData, 'value');

    return map(formattedData, (value, date) => {
      const xValue = isLineChart.value ? date : label;
      return {
        x: xValue,
        y: value,
      };
    });
  }

  function getAccountLabel(accountData) {
    if (accountData?.data_type === DATA_TYPES.BENCHMARK) {
      const bucket =
        INDUSTRY_FOLLOWER_BUCKETS[accountData?.follower_bucket]?.text ||
        accountData?.follower_bucket;
      return `${accountData.name} - ${bucket}`;
    }
    return accountData.name;
  }

  function getAccountAvatar(accountData) {
    if (
      [DATA_TYPES.BRAND, DATA_TYPES.COMPETITOR, DATA_TYPES.CREATOR].includes(accountData?.data_type)
    ) {
      return accountData.avatar;
    }
    return undefined;
  }

  function getDatasetForAccount(accountData) {
    const label = getAccountLabel(accountData);
    const avatar = getAccountAvatar(accountData);

    const alwaysUseAllChannels =
      isCustomMetricReportTile.value ||
      useTags.value ||
      (!isLineChart.value && aggregateBy.value === AGGREGATE_REPORT_TYPES.BRAND.value) ||
      isCommunityReportCategory.value ||
      isListeningReportCategory.value;

    const channelKey = alwaysUseAllChannels
      ? ALL_CHANNELS_KEY
      : getChannelKeyForBucket(aggregateBy.value, null);

    // Extract time series data with fallback logic
    const rawAccountData = extractTimeSeries(
      accountData,
      firstMetric.value,
      channelKey,
      firstChannel.value,
    );

    const data = getXAndYData(rawAccountData, label);
    const accountGraphData = {
      label,
      legend: { avatar },
      data,
      spanGaps: true,
    };

    if (aggregateBy.value === LISTENING_REPORT_AGGREGATE_BY_TYPES.SENTIMENT.value) {
      accountGraphData.borderColor = sentimentColorMap[accountData.name.toUpperCase()];
    }
    const dottedDataTypes = [DATA_TYPES.BENCHMARK, DATA_TYPES.COMPETITOR_AVERAGE];
    if (isLineChart.value && dottedDataTypes.includes(accountData.data_type)) {
      accountGraphData.segment = {
        borderDash: [6, 6],
      };
      if (accountData?.benchmark_type === BENCHMARK_TYPES.INDUSTRY) accountGraphData.stepped = true;
    }
    if (isLineChart.value && accountGraphData.data.length === 1) {
      accountGraphData.pointRadius = 2;
    }
    return accountGraphData;
  }

  function getStackedDatasetForAds() {
    const dataObj = Object.values(formattedReportDataForTableAndGraphReports.value ?? {}).reduce(
      (acc, breakdownData) => {
        const xValue = breakdownData.name;
        const secondaryBreakdown = breakdownData.metrics?.[firstMetric.value] ?? {};
        Object.values(secondaryBreakdown).forEach((secondaryBreakdownData) => {
          const label = secondaryBreakdownData.name;
          if (!acc?.[label]) {
            acc[label] = { label, data: [] };
          }
          acc[label].data.push({
            x: xValue,
            y: secondaryBreakdownData.value,
          });
        });
        return acc;
      },
      {},
    );
    return Object.values(dataObj);
  }

  function addTagNames(data) {
    Object.keys(data).forEach((tagDataStr) => {
      // New backend format (ACCOUNT_TAG_BRAND_TAG_*) includes name directly - skip lookup
      if (tagDataStr.startsWith('ACCOUNT_TAG_') && data[tagDataStr]?.name) {
        return;
      }

      // Old format (44_BRAND) - look up name from store
      const tagId = Number(tagDataStr.split('_')[0] ?? '0');
      if (tagDataStr.includes(`_${DATA_TYPES.BRAND}`)) {
        const lookupName = authStore.brandTagsById?.[tagId]?.name;
        if (lookupName) {
          data[tagDataStr].name = lookupName;
        }
      } else if (tagDataStr.includes(`_${DATA_TYPES.COMPETITOR}`)) {
        const lookupName = competitorTagsById.value?.[tagId]?.name;
        if (lookupName) {
          data[tagDataStr].name = lookupName;
        }
      }
    });
  }

  function getStackedDatasetWithSecondaryAggregate() {
    const datasets = [];

    const allPrimaryBreakdownEntries = Object.entries(
      formattedReportDataForTableAndGraphReports.value ?? {},
    ).filter(([key, value]) => {
      if (key === 'metrics') return false;
      return !(aggregateBy.value && value.data_type !== aggregateBy.value);
    });

    const secondaryKeys = new Map();
    allPrimaryBreakdownEntries.forEach(([, primaryBreakdownData]) => {
      if (primaryBreakdownData.breakdowns) {
        Object.keys(primaryBreakdownData.breakdowns).forEach((key) => {
          secondaryKeys.set(key, primaryBreakdownData.breakdowns[key]);
        });
      }
    });

    secondaryKeys.forEach((secondaryBreakdownData, secondaryKey) => {
      const data = allPrimaryBreakdownEntries
        .filter(([, primaryBreakdownData]) => filterAccountsWithoutAccess(primaryBreakdownData))
        .filter(([, primaryBreakdownData]) => filterCompetitorAverage(primaryBreakdownData))
        .map(([, primaryBreakdownData]) => {
          const breakdownData = primaryBreakdownData.breakdowns?.[secondaryKey] ?? {};
          const { metrics: breakdownMetrics, data_type: breakdownDataType } = breakdownData;
          const channelKey =
            breakdownDataType === DATA_TYPES.CHANNEL ? secondaryKey : ALL_CHANNELS_KEY;
          const value = breakdownMetrics?.[firstMetric.value]?.[channelKey]?.value || 0;
          const xLabel = primaryBreakdownData?.name || primaryBreakdownData?.label;
          return { x: xLabel, y: value };
        });

      const hasNonZeroValue = data.some((point) => point.y !== 0);
      if (hasNonZeroValue) {
        const dataset = {
          label: secondaryBreakdownData?.name || secondaryBreakdownData?.label,
          data,
          spanGaps: true,
        };
        if (secondaryAggregateBy.value === LISTENING_REPORT_AGGREGATE_BY_TYPES.SENTIMENT.value) {
          dataset.borderColor = sentimentColorMap[secondaryKey];
        }
        datasets.push(dataset);
      }
    });

    return datasets;
  }

  function formatHour(hour) {
    return dayjs().hour(hour).minute(0).format('h:mm a');
  }

  function getTimeSeriesStackedDataset() {
    const allPrimaryBreakdownEntries = Object.entries(
      formattedReportDataForTableAndGraphReports.value ?? {},
    ).filter(([key]) => key !== 'metrics');

    // Note: function currently hardcoded for ONLINE_FOLLOWERS
    //  Slices the bars into 8 chunks of 3 hour blocks, 12am-3am, 3am-6am etc
    const barBucketIndexFunc = (hour) => floor(hour / ONLINE_FOLLOWER_HOUR_BUCKET_SIZE); // Replace with other function for non-Online followers/hourly data

    // Collect all unique dates across all breakdowns and sort chronologically
    const allDates = [
      ...new Set(
        allPrimaryBreakdownEntries.flatMap(([, breakdown]) => {
          const metricData =
            breakdown.metrics?.[firstMetric.value]?.[firstChannel.value] ??
            breakdown.metrics?.[firstMetric.value]?.[ALL_CHANNELS_KEY] ??
            {};
          return Object.keys(metricData);
        }),
      ),
    ].sort();

    // Pre-populate buckets with all dates in sorted order and hour components at zero
    const buckets = [];
    for (let hourGroup = 0; hourGroup < 24; hourGroup += ONLINE_FOLLOWER_HOUR_BUCKET_SIZE) {
      const bucketLabel = `${formatHour(hourGroup)} - ${formatHour(hourGroup + ONLINE_FOLLOWER_HOUR_BUCKET_SIZE)}`;
      buckets.push({
        label: bucketLabel,
        spanGaps: true,
        data: allDates.map((date) => ({
          x: formatDatePattern(date, 'MMM D'),
          y: 0,
          components: Array.from({ length: ONLINE_FOLLOWER_HOUR_BUCKET_SIZE }, (_, i) => ({
            label: `${formatHour(hourGroup + i)} - ${formatHour(hourGroup + i + 1)}`,
            value: 0,
          })),
        })),
      });
    }

    allPrimaryBreakdownEntries.forEach(([key, breakdown]) => {
      const metricData =
        breakdown.metrics?.[firstMetric.value]?.[firstChannel.value] ??
        breakdown.metrics?.[firstMetric.value]?.[ALL_CHANNELS_KEY] ??
        {};

      Object.entries(metricData).forEach(([date, bucket]) => {
        if (isNil(bucket)) return;
        const formattedDate = formatDatePattern(date, 'MMM D');
        Object.entries(bucket).forEach(([hourKey, bucketValue]) => {
          const bucketIndex = barBucketIndexFunc(hourKey);
          const slice = buckets[bucketIndex].data.find((dateInfo) => dateInfo.x === formattedDate);
          if (slice) {
            slice.y += bucketValue;
            const hourLabel = `${formatHour(Number(hourKey))} - ${formatHour(Number(hourKey) + 1)}`;
            const component = slice.components.find((c) => c.label === hourLabel);
            if (component) {
              component.value += bucketValue;
            }
          }
        });
      });
    });

    return buckets;
  }

  function getStackedDatasetForAccount() {
    if (isAdsStackedBarChart.value) return getStackedDatasetForAds();

    /*
    Check if secondary aggregate is explicitly set OR if any data entries have breakdowns
    This handles owned reports with non-additive metrics that have breakdowns without
    explicit secondary_aggregate_by in meta
     */
    const hasBreakdowns = Object.values(
      formattedReportDataForTableAndGraphReports.value ?? {},
    ).some((entry) => entry?.breakdowns && Object.keys(entry.breakdowns).length > 0);

    if (secondaryAggregateBy.value || hasBreakdowns) {
      return getStackedDatasetWithSecondaryAggregate();
    }

    return reportChannels.value?.map((channel) => {
      const label = isTSIReportCategory.value
        ? getChannelConfigProp(channel, 'chartLegend') || getChannelText(channel)
        : getChannelText(channel);
      const data = Object.values(reportData.value ?? {})
        .filter((accountData) => filterAccountsWithoutAccess(accountData))
        .filter((accountData) => filterCompetitorAverage(accountData))
        .map((accountData) => ({
          x: accountData.name,
          y: accountData.metrics?.[firstMetric.value]?.[channel]?.value,
        }));
      return { label, data, spanGaps: true };
    });
  }

  function getDatasetForAgeGender() {
    const ageGenderObj = Object.values(
      formattedReportDataForTableAndGraphReports.value ?? {},
    ).reduce((acc, demographicData) => {
      const splitData = demographicData.name.split(' ');
      const label = splitData[0];
      if (!acc?.[label]) {
        acc[label] = {
          label,
          spanGaps: true,
          data: [],
        };
      }
      acc[label].data.push({
        x: splitData[1],
        y: demographicData.metrics?.[firstMetric.value]?.value,
      });
      return acc;
    }, {});
    return Object.values(ageGenderObj);
  }

  function getDatasetForChannel(channel, rootData, labelProperty = 'text') {
    const defaultLabel = getChannelText(channel);
    const label =
      labelProperty === 'text'
        ? defaultLabel
        : getChannelConfigProp(channel, labelProperty) || defaultLabel;

    const rawChannelData =
      rootData?.[channel]?.metrics?.[firstMetric.value]?.[channel] ??
      rootData?.metrics?.[firstMetric.value]?.[channel];
    const data = getXAndYData(rawChannelData, label);
    const dataset = {
      label,
      data,
      ...(isLineChart.value && data.length === 1 && { pointRadius: 2 }),
    };
    return {
      ...dataset,
      spanGaps: true,
    };
  }

  function getDatasetForMetric(accountData) {
    return (metric) => {
      const { mediaTypeLabel } = useMetricDetails({
        metric,
        channels: reportChannels,
        metricReportTypeKey: METRIC_TYPE_REPORT_KEYS.GRAPH_REPORT,
      });
      const rawMetricData = accountData?.metrics?.[metric]?.[firstChannel.value];
      const data = getXAndYData(rawMetricData, mediaTypeLabel.value);
      return {
        label: mediaTypeLabel.value,
        data,
        spanGaps: true,
        ...(isLineChart.value && data.length === 1 && { pointRadius: 2 }),
      };
    };
  }

  const rawGraphReportData = computed(() => {
    // Don't modify the raw data here, formattedReportDataForTableAndGraphReports should
    // be modified to contain additional fields like name, label, etc.
    const data = formattedReportDataForTableAndGraphReports.value ?? {};
    if (useTags.value) addTagNames(data);
    if (isCreatorReport.value) {
      // Stats engine v2 tag-aggregated creator responses include BRAND buckets
      // (no metrics) alongside ACCOUNT_TAG buckets that hold the series. Leave
      // the data untouched so filterAccountsWithTagDataType drops the BRAND
      // buckets by data_type — nulling them here would crash that filter.
      if (canAccessStatsEngineV2.value && useTags.value) {
        return data;
      }
      return Object.entries(data).reduce((acc, [key, value]) => {
        if (Object.values(value?.metrics ?? {}).length === 0) {
          acc[key] = null;
        } else {
          acc[key] = value;
        }
        return acc;
      }, {});
    }
    return data;
  });

  const hasDimensionAgnosticTimeSeriesShape = computed(() => {
    const root = rawGraphReportData.value;
    if (!root || typeof root !== 'object') return false;
    // Total responses (no aggregation) come back with a top-level `metrics`
    // key; grouped responses are keyed by bucket id and surface either a
    // `data_type` field or an ALL_CHANNELS inner key.
    const isTotalReport =
      isNil(aggregateBy.value) ||
      aggregateBy.value === LISTENING_REPORT_AGGREGATE_BY_TYPES.TOTAL.value;
    if (isTotalReport) return !!root.metrics;
    return hasChannelBucketShape(root) || hasAllChannelsInnerKey(root);
  });

  const useDimensionAgnosticLineChart = computed(
    () =>
      isLineChart.value &&
      !isListeningReportCategory.value &&
      !isCustomMetricReportTile.value &&
      hasDimensionAgnosticTimeSeriesShape.value,
  );

  function getGenericDataset() {
    return Object.entries(rawGraphReportData.value).reduce((acc, [groupKey, groupedData]) => {
      // Original logic: determine if this is a TOTAL report (aggregate to single line)
      const isTotalReport = aggregateBy.value !== LISTENING_REPORT_AGGREGATE_BY_TYPES.TOTAL.value;
      const skipGroup =
        (isTotalReport && groupKey === 'metrics') || (!isTotalReport && groupKey !== 'metrics');
      if (skipGroup) return acc;

      const isAggregatedByChannel =
        aggregateBy.value === BASE_BREAKDOWN_OPTIONS.CHANNEL.value ||
        (isNil(aggregateBy.value) && reportChannels.value?.length > 1);

      // Channel-breakdown responses include a brand-level bucket alongside the
      // per-channel buckets; drop off-dimension buckets so they don't show up as
      // stray legend entries.
      if (
        isAggregatedByChannel &&
        groupKey !== 'metrics' &&
        groupedData?.data_type !== DATA_TYPES.CHANNEL
      ) {
        return acc;
      }

      // Creator reports include brand info, but we only want to show creators
      if (isCreatorReport.value && groupedData?.data_type === DATA_TYPES.BRAND) {
        return acc;
      }

      // Tag-based breakdown responses include brand buckets for informational purposes;
      // filter to only include items with matching data_type.
      // TAG aggregation is special: aggregate_by is 'TAG' but data_type is 'ACCOUNT_TAG' or 'TAG'
      if (
        TAG_BASED_AGGREGATE_TYPES.includes(aggregateBy.value) &&
        groupKey !== 'metrics' &&
        !TAG_BASED_DATA_TYPES.includes(groupedData?.data_type)
      ) {
        return acc;
      }

      // For other breakdown types (CONTENT_TAG, MEDIA_TYPE, etc.), filter to matching data_type
      // BRAND aggregation is excluded as it includes competitor data
      const allowedDataTypes = getAllowedDataTypes(aggregateBy.value);
      if (
        allowedDataTypes &&
        !isAggregatedByChannel &&
        !TAG_BASED_AGGREGATE_TYPES.includes(aggregateBy.value) &&
        groupKey !== 'metrics' &&
        !shouldIncludeBucket(groupedData, allowedDataTypes)
      ) {
        return acc;
      }

      let rawData;
      if (groupKey === 'metrics') {
        rawData = groupedData[firstMetric.value]?.[ALL_CHANNELS_KEY];
      } else {
        // Use bucket key as channel for CHANNEL aggregation (explicit or implicit)
        const channelKey = isAggregatedByChannel ? groupKey : ALL_CHANNELS_KEY;
        rawData = groupedData.metrics?.[firstMetric.value]?.[channelKey];
      }

      // For TOTAL aggregation (groupKey === 'metrics'), use the metric display name as the line label
      // This ensures proper labeling when there's no dimensional breakdown
      const label =
        groupKey === 'metrics' ? reportMetricDetails.value?.displayName : groupedData?.name;
      const data = getXAndYData(rawData, label);

      const graphData = {
        label,
        data,
        spanGaps: true,
      };

      const avatar = getAccountAvatar(groupedData);
      if (avatar) {
        graphData.legend = { avatar };
      }

      if (aggregateBy.value === LISTENING_REPORT_AGGREGATE_BY_TYPES.SENTIMENT.value) {
        graphData.borderColor = sentimentColorMap[groupKey];
      }

      if (isLineChart.value && data.length === 1) {
        graphData.pointRadius = 2;
      }

      acc.push(graphData);
      return acc;
    }, []);
  }

  const { horizontalBarChartData, horizontalBarChartOptions, showHorizontalLegend } =
    useHorizontalBarReportDetails({
      rawGraphReportData,
      report,
    });

  const showLegend = computed(
    () =>
      (aggregateBy.value !== LISTENING_REPORT_AGGREGATE_BY_TYPES.TOTAL.value &&
        !isHorizontalBarChart.value) ||
      (showHorizontalLegend.value && isHorizontalBarChart.value) ||
      isMultiMetric.value,
  );

  const graphReportData = computed(() => {
    if (isTimeSeriesStackedBarChart.value) {
      return getTimeSeriesStackedDataset();
    }
    if (isStackedBarChart.value) {
      return getStackedDatasetForAccount();
    }
    if (useDimensionAgnosticLineChart.value) {
      return getGenericDataset();
    }
    if (useChannelDataset.value) {
      const rootData =
        isLineChart.value && !isCommunityReportCategory.value && !isListeningReportCategory.value
          ? (Object.values(rawGraphReportData.value)?.[0] ?? {})
          : rawGraphReportData.value;
      return reportChannels.value.map((channel) =>
        getDatasetForChannel(channel, rootData, isTSIReportCategory.value ? 'chartLegend' : 'text'),
      );
    }
    if (useMetricDataset.value) {
      const accountData = Object.values(rawGraphReportData.value)?.[0] ?? {};
      return metrics.value.map(getDatasetForMetric(accountData));
    }
    if (useAgeGenderDataset.value) {
      return getDatasetForAgeGender();
    }
    if (isListeningReportCategory.value) {
      return getGenericDataset();
    }
    const filteredData = Object.values(rawGraphReportData.value)
      .filter((accountData) => filterAccountsWithTagDataType(accountData))
      .filter((accountData) => filterAccountsWithoutAccess(accountData))
      .filter((accountData) => filterCompetitorAverage(accountData))
      .filter((accountData) => filterOutBrandForCreatorReport(accountData));
    return filteredData.map((accountData) => getDatasetForAccount(accountData));
  });

  function sortStackedChartData(chartData, sort = 'DESC', maxBars = null) {
    // Get totals for each bar
    const totals = chartData.reduce((acc, bar) => {
      bar.data.forEach((slice) => {
        if (!acc[slice.x]) {
          acc[slice.x] = slice.y;
        } else {
          acc[slice.x] += slice.y;
        }
      });
      return acc;
    }, {});

    // Sort by the height of each bar
    const resultData = cloneDeep(chartData);
    if (!disableAggregationSort.value) {
      resultData.forEach((bar) => {
        bar.data.sort((a, b) => {
          const aTotal = totals[a.x] ?? 0;
          const bTotal = totals[b.x] ?? 0;
          if (sort.toLowerCase() === 'asc') {
            return aTotal - bTotal;
          }
          if (sort.toLowerCase() === 'desc') {
            return bTotal - aTotal;
          }
          return 0;
        });
      });
    }
    // Optionally slice to desired number of bars
    if (isNumber(maxBars)) {
      resultData.forEach((bar) => {
        bar.data = bar.data.slice(0, maxBars);
      });
    }

    return resultData;
  }

  const prioritizedGraphReportData = computed(() => {
    if (isHorizontalBarChart.value) return horizontalBarChartData.value;
    if (isStackedBarChart.value) {
      if (isTimeSeriesStackedBarChart.value) return graphReportData.value;
      return sortStackedChartData(graphReportData.value, 'DESC', CHART_DATA_LIMIT);
    }
    const sortedData = disableAggregationSort.value
      ? graphReportData.value
      : sortChartData(graphReportData.value);
    return sortedData.slice(0, CHART_DATA_LIMIT);
  });

  function calculateDurationStepSize(min, max) {
    if (min === max) return 1;
    const range = max - min;
    const numSteps = 5;
    const step = Math.ceil(range / numSteps);

    if (range < 60 * numSteps) {
      if (range <= 30) {
        return 5;
      }
      if (range <= 60) {
        return 10;
      }
      if (range <= 150) {
        return 15;
      }
      return 30;
    }
    if (range < 3600 * numSteps) {
      return Math.ceil(step / 60) * 60;
    }
    return Math.ceil(step / 3600) * 3600;
  }

  const chartStepSize = computed(() => {
    if (!isDurationMetric.value || !prioritizedGraphReportData.value.length) {
      return undefined;
    }

    const allValues = [];
    prioritizedGraphReportData.value.forEach((dataset) => {
      dataset.data.forEach((point) => {
        allValues.push(point.y);
      });
    });

    allValues.push(...goalValues.value);

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);
    return calculateDurationStepSize(min, max);
  });

  const lineChartOptions = computed(() => ({
    scales: {
      x: {
        type: 'time',
        time: {
          tooltipFormat: TIME_FORMATS?.[timeUnit.value] || DEFAULT_TIME_FORMAT,
          unit: timeUnit.value || 'day',
        },
      },
      y: {
        ticks: {
          callback: (value) => getFormattedValue(value, false),
          ...(chartStepSize.value && { stepSize: chartStepSize.value }),
        },
      },
    },
    ...goalLineOptions.value,
  }));

  const stackedBarChartOptions = computed(() => ({
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        ticks: {
          callback: (value) => getFormattedValue(value),
          ...(chartStepSize.value && { stepSize: chartStepSize.value }),
        },
      },
    },
    ...goalLineOptions.value,
  }));

  const barChartOptions = computed(() => ({
    grouped: useAgeGenderDataset.value,
    scales: {
      y: {
        ticks: {
          callback: (value) => getFormattedValue(value, false),
          ...(chartStepSize.value && { stepSize: chartStepSize.value }),
        },
      },
    },
    ...goalLineOptions.value,
  }));

  const chartOptions = computed(() => {
    if (isLineChart.value) return lineChartOptions.value;
    if (isHorizontalBarChart.value) return horizontalBarChartOptions.value;

    if (isStackedBarChart.value) return stackedBarChartOptions.value;
    if (isBarChart.value) return barChartOptions.value;
    return {};
  });

  function timeSeriesGroupedStackedBarTooltipCallback(context) {
    let datasetIndex = 0;
    const components = context.tooltip?.dataPoints
      ?.map((slice) =>
        slice.raw.components.map((component, i) => {
          datasetIndex = slice.datasetIndex;
          return {
            label: component.label,
            value: getFormattedValue(component.value, false),
            divider: i === 0, // divider for title
          };
        }),
      )
      .flat();
    const minHour = datasetIndex * ONLINE_FOLLOWER_HOUR_BUCKET_SIZE;
    const maxHour = minHour + ONLINE_FOLLOWER_HOUR_BUCKET_SIZE;
    const titleLabel = `${context.tooltip.title[0]}, ${formatHour(minHour)} - ${formatHour(maxHour)}`;
    const title = [{ label: titleLabel, value: '' }];

    return title.concat(components);
  }

  const graphCustomTooltipCallback = computed(() => {
    if (isTimeSeriesStackedBarChart.value) {
      return timeSeriesGroupedStackedBarTooltipCallback;
    }
    return undefined;
  });

  function generateCustomTooltip({ tooltip }) {
    if (!tooltip) return {};
    const dataPoint = tooltip?.dataPoints?.[0];
    if (!dataPoint) return {};

    let label = '';
    if (isCustomMetricReportTile.value) {
      label = customMetricDetails.value.name;
    } else {
      label = reportMetricDetails.value.displayName;
    }

    return {
      title: tooltip.title[0],
      dataPoints: [
        {
          label,
          value: getFormattedValue(Number(dataPoint.raw.y), true),
        },
      ],
    };
  }

  return {
    chartOptions,
    chartFormatConfig,
    generateCustomTooltip,
    goalValues,
    graphType,
    isStackedBarChart,
    prioritizedGraphReportData,
    showLegend,
    graphCustomTooltipCallback,
  };
}
